import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MagicButton } from '../ui';
import trLogoWhite from '../../../assets/tr-logo-long-white.webp';
import Section from '../layout/Section';

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Animation variants for text reveals
  const titleVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const descriptionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.9,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  return (
    <div 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      role="banner"
      aria-label="Tom Robotics hero section"
    >
      {/* Animated Background Elements - Sensor Network Representation */}
      <div 
        className="absolute inset-0 overflow-hidden"
        aria-hidden="true"
        role="presentation"
      >
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(37, 99, 235, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(37, 99, 235, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Floating Sensor Nodes - Reduced on mobile */}
        {Array.from({ length: typeof window !== 'undefined' && window.innerWidth < 768 ? 6 : 12 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full shadow-lg shadow-blue-500/50"
            initial={{
              x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
              y: typeof window !== 'undefined' ? Math.random() * window.innerHeight : 0,
              opacity: 0
            }}
            animate={{
              x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
              y: typeof window !== 'undefined' ? Math.random() * window.innerHeight : 0,
              opacity: [0, 0.8, 0.4, 0.8, 0.3]
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: i * 0.5,
              ease: "linear"
            }}
          />
        ))}

        {/* Connection Lines */}
        <svg className="absolute inset-0 w-full h-full">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.line
              key={i}
              x1={`${20 + i * 12}%`}
              y1="20%"
              x2={`${30 + i * 10}%`}
              y2="80%"
              stroke="rgba(37, 99, 235, 0.2)"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: [0, 1, 0.5, 1],
                opacity: [0, 0.6, 0.3, 0.6]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut"
              }}
            />
          ))}
        </svg>

        {/* Pulsing Circles - Representing Sensor Coverage - Fewer on mobile */}
        {Array.from({ length: typeof window !== 'undefined' && window.innerWidth < 768 ? 3 : 6 }).map((_, i) => (
          <motion.div
            key={`pulse-${i}`}
            className="absolute border border-blue-500/20 rounded-full"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + i * 12}%`,
              width: typeof window !== 'undefined' && window.innerWidth < 768 
                ? `${60 + i * 30}px` 
                : `${100 + i * 50}px`,
              height: typeof window !== 'undefined' && window.innerWidth < 768 
                ? `${60 + i * 30}px` 
                : `${100 + i * 50}px`
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 1.2, 0.8, 1],
              opacity: [0, 0.4, 0.2, 0.4]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Data Flow Animation */}
        <motion.div
          className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
            repeatDelay: 2
          }}
        />
        <motion.div
          className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"
          initial={{ x: "100%" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "linear",
            repeatDelay: 1
          }}
        />
      </div>

      {/* Main Content */}
      <Section disableContainer className="relative z-10">
        <div className="text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-4 sm:mb-6 flex justify-center"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            <picture>
              <source srcSet={trLogoWhite} type="image/webp" />
              <img
                src={trLogoWhite}
                alt="Tom Robotics"
                width={512}
                height={128}
                className="h-16 xs:h-20 sm:h-24 md:h-28 lg:h-32 w-auto max-w-full"
                role="img"
                aria-label="Tom Robotics company logo"
                decoding="async"
              />
            </picture>
          </motion.div>

          <h1 className="sr-only">{t('hero.title', 'TOM Robotics')}</h1>

          <motion.p
            className="text-lg xs:text-xl sm:text-xl md:text-2xl lg:text-3xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto font-light leading-relaxed"
            initial="hidden"
            animate="visible"
            variants={subtitleVariants}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2"
            initial="hidden"
            animate="visible"
            variants={descriptionVariants}
          >
            {t('hero.description')}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={ctaVariants}
            className="px-4 flex flex-col xs:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <MagicButton
              size="lg"
              variant="primary"
              className="text-base sm:text-lg w-full xs:w-auto"
              onClick={() => {
                document.getElementById('technology')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }}
              aria-label="Navigate to products section to explore Tom Robotics technology"
            >
              {t('hero.cta')}
            </MagicButton>
            <MagicButton
              size="lg"
              variant="outline"
              className="text-base sm:text-lg w-full xs:w-auto"
              onClick={() => navigate('/univais')}
            >
              {t('hero.univaisCta')}
            </MagicButton>
          </motion.div>
        </div>
      </Section>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}  
        aria-label="Scroll down to continue"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById('technology')?.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        onClick={() => {
          document.getElementById('technology')?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center"
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-3 bg-gray-400 rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Hero;